import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { CalendarClock, Fingerprint, UserCheck, LogOut, UserX, Users, ArrowRight } from 'lucide-react';
import { useUser } from '@/shared/hooks/useAuth';
import { getApiErrorMessage } from '@/shared/api/axios';
import {
  useTodayLog,
  useTeamAttendance,
  useAttendanceMutations,
} from '../hooks/useAttendance';
import { MyAttendanceCard } from '../components/MyAttendanceCard';
import { TeamAttendance } from '../components/TeamAttendance';
import { BiometricDateWiseCard } from '../components/BiometricDateWiseCard';
import { StatTile } from '../components/StatTile';
import { memberState, type BreakType } from '../types';
import { isSuperAdminOrOwner } from '@jira-clone/shared-types';
import { ROUTES } from '@/routes/paths';

export function AttendancePage() {
  const user = useUser();
  const r = String(user?.role ?? '').toLowerCase();
  const isPrivileged =
    isSuperAdminOrOwner(user?.role) ||
    ['admin', 'superadmin', 'owner', 'manager'].includes(r) ||
    String(user?.department ?? '').toLowerCase() === 'hr';

  const { data: today, isLoading: todayLoading } = useTodayLog();
  const { data: team = [], isLoading: teamLoading, isError: teamError } = useTeamAttendance(isPrivileged as boolean);
  const { checkIn, checkOut, startBreak, endBreak } = useAttendanceMutations();

  // Real, derived-only team summary — no extra fetch, folded over the same
  // roster TeamAttendance renders below.
  const teamStats = useMemo(() => {
    let present = 0;
    let checkedOut = 0;
    let notIn = 0;
    for (const m of team) {
      const s = memberState(m);
      if (s === 'present') present++;
      else if (s === 'checked_out') checkedOut++;
      else notIn++;
    }
    return { present, checkedOut, notIn, total: team.length };
  }, [team]);

  const busy = checkIn.isPending || checkOut.isPending || startBreak.isPending || endBreak.isPending;

  const handleCheckIn = () =>
    checkIn.mutate(undefined, {
      onSuccess: (d) => toast.success(`Checked in at ${d.time}`),
      onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to check in.')),
    });

  const handleCheckOut = () =>
    checkOut.mutate(undefined, {
      onSuccess: (d) => toast.success(`Checked out at ${d.time} · ${d.hours}h`),
      onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to check out.')),
    });

  const handleStartBreak = (type: BreakType) =>
    startBreak.mutate(type, {
      onSuccess: () => toast.success(`Started ${type} break`),
      onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to start break.')),
    });

  const handleEndBreak = () =>
    endBreak.mutate(undefined, {
      onSuccess: (d) => toast.success(`Break ended (${Math.round((d.duration || 0) / 60)} min)`),
      onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to end break.')),
    });

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto pr-1">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200">
            <CalendarClock className="h-6 w-6 text-white" />
          </div>
          <div>
            <span className="block text-[11px] font-bold tracking-wider text-[#6825F5] uppercase">
              ATTENDANCE
            </span>
            <h1 className="text-xl md:text-2xl font-bold tracking-tight text-[#11183F]">
              Check in, take breaks, and track your hours.
            </h1>
          </div>
        </div>
        {isPrivileged && (
          <Link to={ROUTES.biometricAdmin} className="self-start sm:self-auto">
            <button
              type="button"
              className="inline-flex items-center gap-2.5 rounded-full bg-[#EDE9FE] px-5 py-2.5 text-xs font-semibold text-[#6825F5] transition-all hover:bg-[#DDD6FE] hover:shadow-sm"
            >
              <Fingerprint className="h-4 w-4 text-[#6825F5]" />
              <span>Biometric devices &amp; attendance</span>
              <ArrowRight className="h-3.5 w-3.5 text-[#6825F5]" />
            </button>
          </Link>
        )}
      </div>

      {/* Summary stats — today's team roster, folded from the same data TeamAttendance renders below */}
      {isPrivileged && !teamLoading && !teamError && team.length > 0 && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatTile
            icon={Users}
            label="Team Size"
            value={teamStats.total}
            tone="primary"
            index={0}
          />
          <StatTile
            icon={UserCheck}
            label="Present Today"
            value={teamStats.present}
            tone="success"
            sublabel="On duty"
            index={1}
          />
          <StatTile
            icon={LogOut}
            label="Checked Out"
            value={teamStats.checkedOut}
            tone="info"
            sublabel={teamStats.checkedOut === 0 ? 'No one checked out' : `${teamStats.checkedOut} checked out`}
            index={2}
          />
          <StatTile
            icon={UserX}
            label="Not Checked In"
            value={teamStats.notIn}
            tone="warning"
            sublabel="Pending"
            index={3}
          />
        </div>
      )}

      {/* Section 1 â€” My Attendance (always visible) */}
      <MyAttendanceCard
        today={today}
        loading={todayLoading}
        busy={busy}
        onCheckIn={handleCheckIn}
        onCheckOut={handleCheckOut}
        onStartBreak={handleStartBreak}
        onEndBreak={handleEndBreak}
      />

      {/* Section 2 — Biometric attendance, from the machines */}
      {isPrivileged && <BiometricDateWiseCard />}

      {/* Section 3 — Team Attendance */}
      {isPrivileged && <TeamAttendance team={team} loading={teamLoading} isError={teamError} />}
    </div>
  );
}
